import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setLoading(false);
        toast.error('Payment verification timed out');
        return;
      }

      try {
        const response = await api.get(`/payments/stripe/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus(response.data);
          setLoading(false);
          toast.success('Payment successful!');
          return;
        }

        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl mb-4">Verifying payment...</div>
          <div className="text-muted-foreground">Please wait while we confirm your transaction</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto text-center">
        <div className="glass-card p-12">
          <CheckCircle2 className="w-20 h-20 text-accent mx-auto mb-6" data-testid="success-icon" />
          
          <h1 className="text-4xl font-bold mb-4" data-testid="success-title">
            Payment Successful!
          </h1>
          
          <p className="text-muted-foreground text-lg mb-8">
            Thank you for your purchase. Your order has been confirmed and you'll receive access to your digital products shortly.
          </p>

          {status && (
            <div className="mb-8 p-4 bg-white/5 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Transaction ID</p>
              <p className="font-mono text-sm">{sessionId}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/my-orders"
              className="btn-primary"
              data-testid="view-orders-button"
            >
              View My Orders
            </Link>
            <Link
              to="/products"
              className="px-8 py-4 bg-white/5 text-foreground border border-white/10 hover:bg-white/10 transition-all duration-300 rounded-full font-semibold"
              data-testid="continue-shopping-button"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
